import { Box, Stack, Typography, alpha } from '@mui/material';
import { CheckCircleFilled, ClockCircleOutlined } from '@ant-design/icons';
import { Article } from 'types/blog';
import { estimateReadingMinutes } from 'const/blog';
import { inline } from './InlineText';

// ==============================|| この記事のポイント ||============================== //
//
// 本文の一番上、目次より前に置く結論のまとめ。
//
// 検索から来た人は全文を読まない前提で作っている。
//   - 知りたいことが1つだけの人は、ここだけ読んで帰れる
//   - 詳しく知りたい人は、続く目次から必要な章に飛ぶ
// という二択を冒頭で提示することで、「長い記事だ」と判断された瞬間の離脱を減らす狙い。
//
// 読了目安を出しているのは、長文であることを先に伝えて心づもりをしてもらうため。
// 隠しても読み進めれば分かることなので、先に出したほうが信頼される。

const primaryColor = '#2aa1af';

const ArticleSummary = ({ article }: { article: Article }) => (
    <Box
        component="aside"
        aria-label="この記事のポイント"
        sx={{
            mt: { xs: 5, md: 6 },
            borderRadius: 3,
            overflow: 'hidden',
            border: `2px solid ${alpha(primaryColor, 0.35)}`
        }}
    >
        <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: { xs: 2.5, md: 3.5 }, py: { xs: 1.75, md: 2 }, bgcolor: primaryColor }}
        >
            <Typography component="p" sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '1rem', md: '1.1rem' }, letterSpacing: '0.02em' }}>
                この記事のポイント
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: alpha('#fff', 0.9), flexShrink: 0 }}>
                <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                    <ClockCircleOutlined />
                </Box>
                <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    約{estimateReadingMinutes(article)}分
                </Typography>
            </Stack>
        </Stack>

        <Box component="ul" sx={{ m: 0, px: { xs: 2.5, md: 3.5 }, py: { xs: 2.5, md: 3 }, listStyle: 'none', bgcolor: alpha(primaryColor, 0.05) }}>
            {article.summary.map((point, index) => (
                <Box
                    component="li"
                    key={index}
                    sx={{
                        display: 'flex',
                        // 折り返して2行以上になる項目でも、チェックを1行目の高さに合わせる
                        // (既定の stretch のままだと、アイコンが項目の上下中央に落ちてしまう)
                        alignItems: 'flex-start',
                        gap: 1.25,
                        mb: index === article.summary.length - 1 ? 0 : 1.75,
                        color: '#374151',
                        fontSize: { xs: '0.93rem', md: '0.98rem' },
                        lineHeight: 1.9
                    }}
                >
                    <Box aria-hidden="true" sx={{ color: primaryColor, fontSize: '0.9rem', mt: '0.42em', flexShrink: 0, display: 'flex' }}>
                        <CheckCircleFilled />
                    </Box>
                    <Box component="span">{inline(point)}</Box>
                </Box>
            ))}
        </Box>
    </Box>
);

export default ArticleSummary;
